import React from 'react';
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faClone,
} from '@fortawesome/pro-regular-svg-icons';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { useThenableModal } from '@client/components/common/Modal';
import { HideWarningModal } from '../Modals';
import { ColorPicker } from '../ColorPicker';
import { useFocusWithin } from 'react-aria';
import { DeleteSection } from './DeleteSection';
import { ClickableIcon } from '@client/new/components/basic';
import classNames from 'classnames';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { AddSection } from './AddSection';
import { ISection } from '../schemas';
import { F0, F1 } from '@client/new/helpers/types';
import { Typography } from '@design-system/components/typography/typography';

type Props = {
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  color: string;
  colors: string[];
  title: string;
  alwaysEnabled?: boolean;
  enabled?: boolean;
  folded: boolean;
  hasError: boolean;
  id?: string;

  onChangeEnabled?: F1<boolean>;
  onColorIndexChange?: F1<number>;
  onMove?: F1<number>;
  onDeleteSection?: F0;
  onDuplicateSection?: F0;
  onAddSection?: F1<ISection['type']>;
  setFolded: F1<boolean>;

  children: () => React.ReactNode;
};

export const SectionContainer = ({
  children,
  title,
  alwaysEnabled,
  enabled,
  onChangeEnabled,
  onMove,
  colors,
  color,
  onColorIndexChange,
  canMoveUp = false,
  canMoveDown = false,
  onDeleteSection,
  onDuplicateSection,
  onAddSection,
  folded,
  hasError,
  setFolded,
  id,
}: Props) => {
  const hideWarningModal = useThenableModal(HideWarningModal);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setFolded(false);
    },
  });

  const handleHideToggleChange = async (val: boolean) => {
    if (val) {
      setFolded(false);
      return onChangeEnabled && onChangeEnabled(true);
    }

    const choice = await hideWarningModal.open();
    if (choice) {
      setFolded(true);
      return onChangeEnabled && onChangeEnabled(false);
    }
  };

  const handleColorChange = (val: string) => {
    const index = colors.findIndex((c) => c === val);
    onColorIndexChange && onColorIndexChange(index);
  };

  return (
    <>
      <div className="bg-white mb-6 rounded-lg" id={id} {...focusWithinProps}>
        <div
          className={`
          w-full flex flex-wrap bg-[#E1E5F0] ${
            enabled ? '' : 'opacity-55'
          } items-center justify-between lg:h-[70px]
          ${folded ? 'rounded-lg' : 'rounded-t-lg'}
        `}
        >
          <div
            className="flex items-center w-full md:w-auto h-[70px] justify-between px-2.5"
            onClick={() => setFolded(!folded)}
            style={{ userSelect: 'none', cursor: 'pointer' }}
          >
            <div style={{ padding: '0 12px 0 8px', marginTop: '4px' }}>
              <ClickableIcon
                title={folded ? 'Expand' : 'Collapse'}
                icon={folded ? faChevronUp : faChevronDown}
              />
            </div>
            <Typography
              className="overflow-clip text-ellipsis max-w-[150px] 2xl:max-w-none whitespace-nowrap"
              variant="title"
              fontWeight="medium"
              size="large"
              {...(hasError ? { color: 'ERROR-100' } : {})}
            >
              {title}
            </Typography>
            <div className="md:hidden">
              <ColorPicker
                disableHex
                disableLabels
                value={color || '#FFFFFF'}
                onChange={handleColorChange}
                error={false}
                colors={colors}
                borderColor="#6367EF"
              />
            </div>
          </div>
          <div className="hidden md:flex flex-wrap items-center h-full">
            {
              <>
                <div className="ml-8 mr-8 flex items-center">
                  <div className="hidden md:block mr-4">Background: </div>
                  <ColorPicker
                    disableHex
                    disableLabels
                    value={color || '#FFFFFF'}
                    onChange={handleColorChange}
                    error={false}
                    colors={colors}
                    borderColor="#6367EF"
                  />
                </div>
                <div className="border-r border-white h-full" />
              </>
            }
            {alwaysEnabled ? null : (
              <ActionBox>
                <Toggle
                  value={Boolean(enabled)}
                  onChange={handleHideToggleChange}
                />
              </ActionBox>
            )}
            <div className="border-r border-white h-full" />
            {onDuplicateSection && (
              <ActionBox>
                <ClickableIcon
                  title="Duplicate"
                  icon={faClone}
                  onClick={onDuplicateSection}
                />
              </ActionBox>
            )}
            {onDeleteSection && (
              <ActionBox>
                <DeleteSection onDelete={onDeleteSection} />
              </ActionBox>
            )}
            <ActionBox
              style={{
                opacity: canMoveUp ? 1 : 0.2,
                cursor: canMoveUp ? 'pointer' : 'default',
              }}
              onClick={() => canMoveUp && onMove && onMove(-1)}
            >
              <ClickableIcon icon={faArrowUp} title="Move up" />
            </ActionBox>
            <ActionBox
              style={{
                opacity: canMoveDown ? 1 : 0.2,
                cursor: canMoveDown ? 'pointer' : 'default',
              }}
              onClick={() => canMoveDown && onMove && onMove(1)}
            >
              <ClickableIcon icon={faArrowDown} title="Move down" />
            </ActionBox>
          </div>

          <div className="w-full flex justify-end">
            <ToggleButtonGroup
              value={'left'}
              exclusive
              className="md:hidden"
              style={{ background: 'transparent' }}
            >
              {onDeleteSection && (
                <ToggleButton value="left">
                  <DeleteSection onDelete={onDeleteSection} />
                </ToggleButton>
              )}
              {alwaysEnabled ? null : (
                <ToggleButton value="left">
                  <Toggle
                    value={Boolean(enabled)}
                    onChange={handleHideToggleChange}
                  />
                </ToggleButton>
              )}
              {onDuplicateSection && (
                <ToggleButton value="left">
                  <ClickableIcon
                    title="Duplicate"
                    icon={faClone}
                    onClick={onDuplicateSection}
                  />
                </ToggleButton>
              )}
              <ToggleButton
                value="left"
                onClick={() => canMoveUp && onMove && onMove(-1)}
              >
                <ClickableIcon icon={faArrowUp} title="Move up" />
              </ToggleButton>
              <ToggleButton
                value="left"
                onClick={() => canMoveDown && onMove && onMove(1)}
              >
                <ClickableIcon icon={faArrowDown} title="Move down" />
              </ToggleButton>
              <ToggleButton value="left" onClick={() => setFolded(!folded)}>
                <ClickableIcon
                  title={folded ? 'Expand' : 'Collapse'}
                  icon={folded ? faChevronUp : faChevronDown}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>
        <div
          className={`${
            folded
              ? 'max-h-0 overflow-hidden'
              : 'block p-[30px] pt-[20px] ' +
                (enabled ? 'bg-white' : 'bg-gray-300 opacity-70')
          }`}
          style={{
            borderBottomRightRadius: '12px',
            borderBottomLeftRadius: '12px',
          }}
        >
          {folded ? null : children()}
        </div>
      </div>
      <hideWarningModal.Popup />
      {onAddSection && (
        <div className="flex w-full my-6">
          <div className="flex flex-grow items-center">
            <div className="border-t-[1px] border-primary flex-grow" />
          </div>
          <div className="w-10 flex justify-center mx-6">
            <AddSection onAdd={onAddSection} />
          </div>
          <div className="flex flex-grow items-center">
            <div className="border-t-[1px] border-primary flex-grow" />
          </div>
        </div>
      )}
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ActionBox({ children, className, ...props }: any) {
  return (
    <>
      <div
        {...props}
        className={classNames(
          'flex w-[75px] items-center justify-center',
          className,
        )}
      >
        {children}
      </div>
      <div className="border-r border-white h-full" />
    </>
  );
}
